import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { getColorInfo, possibleColorShops } from "@okeys/shared-utils"
import { ColorWheelIcon } from "@radix-ui/react-icons"
import React, { useState } from "react"

type EditThemeColorModalProps = {
  isOpen: boolean
  currentColor: string
  onSave: (newThemeData: { color: string }) => void
  onClose: () => void
}

export const EditThemeColorModal: React.FC<EditThemeColorModalProps> = ({
  isOpen,
  currentColor,
  onSave,
  onClose,
}) => {
  const [selectedColor, setSelectedColor] = useState<string>(currentColor)

  const handleSave = () => {
    onSave({ color: selectedColor })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <ColorWheelIcon className="h-5 w-5 mr-2 text-slate-600" />
            Modifier le thème de couleur
          </DialogTitle>
          <DialogDescription>
            Choisissez une couleur qui représente l'identité visuelle de votre
            boutique.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Affichage de la couleur actuelle */}
          <div className="space-y-3">
            <Label>Couleur actuelle</Label>
            <div className="flex items-center space-x-3 p-3 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800/50">
              <div
                className="w-full h-16 rounded-lg border border-slate-300 flex-shrink-0 shadow-sm"
                style={{ backgroundColor: getColorInfo(currentColor)?.primary }}
              />
            </div>
          </div>

          {/* Sélection de nouvelle couleur */}
          <div className="space-y-3">
            <Label>Choisir une nouvelle couleur</Label>
            <div className="grid grid-cols-6 gap-3">
              {possibleColorShops.map((color) => (
                <button
                  key={color.name}
                  onClick={() => setSelectedColor(color.primary)}
                  className={`relative w-12 h-12 rounded-lg border-2 transition-all duration-200 hover:scale-110 hover:shadow-lg ${
                    selectedColor === color.primary
                      ? "border-slate-800 dark:border-slate-200 shadow-lg scale-105"
                      : "border-slate-300 dark:border-slate-600 hover:border-slate-400"
                  }`}
                  style={{ backgroundColor: color.primary }}
                  title={`${color.category} - ${color.name.replace(/_/g, " ")}`}
                >
                  {selectedColor === color.primary && (
                    <div className="absolute inset-0 rounded-lg border-2 border-white shadow-inner" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Aperçu de la couleur sélectionnée */}
          {selectedColor !== currentColor && (
            <div className="space-y-3">
              <Label>Aperçu de la nouvelle couleur</Label>
              <div className="flex items-center space-x-3 p-3 border border-slate-200 dark:border-slate-700 rounded-md bg-blue-50 dark:bg-blue-900/20">
                <div
                  className="w-full h-16 rounded-lg border border-slate-300 flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: selectedColor }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-start pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={selectedColor === currentColor}
          >
            Sauvegarder les modifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
